import React, { useEffect, useRef, useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import SideNavBar from "./SideNavBar";
import menu from "../../assets/admin/menu-left.svg";
import logo from "../../assets/admin/logo.svg";
import logoName from "../../assets/admin/Name.svg";
import { toast, ToastContainer } from "react-toastify";
import {
  loadBrandList,
  loadProductList,
} from "../../constants/FetchConstant";

const AdminLayout = () => {
  
  useEffect(() => {
    const handleHideSidebar = () => {
      if (window.innerWidth < 768) {
        setIsSidebarVisible(false);
      }
    };
    window.addEventListener('hideSidebar', handleHideSidebar);
    return () => {
      window.removeEventListener('hideSidebar', handleHideSidebar);
    };
  }, []);
  const navigate = useNavigate();
  const dropdownRef = useRef(null);

  const [isSidebarVisible, setIsSidebarVisible] = useState(true);
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);

  const toggleSidebar = () => {
    setIsSidebarVisible(!isSidebarVisible);
  };

  const username = localStorage.getItem("username") || "User";

  const user = {
    userName: username,
  };

  const toggleDropdown = () => setIsDropdownVisible(!isDropdownVisible);

  const handleSignOut = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/logout`,
        {
          method: "GET",
          credentials: "include", 
        }
      );

      if (response.ok) {
        toast.success("Signed out successfully");
      } else {
        toast.error("Server failed to log out");
      }
    } catch (error) {
      toast.error("Failed to sign out. Please try again.");
    }

    // Always clear client storage and navigate regardless
    localStorage.clear();
    sessionStorage.clear();
    navigate("/signin");
  };

  // Handle clicks outside the dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownVisible(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const checkAuthAndLoadData = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/validate-token`,
          {
            credentials: "include",
          }
        );

        if (response.ok) {
          // Authenticated → load data
          loadProductList();
          loadBrandList();
        } else {
          throw new Error("Unauthorized");
        }
      } catch (error) {
        localStorage.clear();
        sessionStorage.clear();
        navigate("/signin");
      }
    };

    checkAuthAndLoadData();
  }, []);

  return (
  <div className="flex h-screen overflow-hidden bg-white overflow-x-hidden">
      {/* Desktop sidebar - always visible */}
      <div className="relative overflow-visible hidden md:block">
        <SideNavBar isSidebarVisible={isSidebarVisible} />
      </div>
      {/* Mobile sidebar overlay - only visible when toggled */}
      {isSidebarVisible && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-40 md:hidden">
          <div className="absolute top-0 left-0 h-full w-[70vw] max-w-[350px] bg-white shadow-lg flex flex-col p-0">
            <div className="flex items-center px-4 py-4">
              <button
                className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
                onClick={toggleSidebar}
                aria-label="Close sidebar"
              >
                &times;
              </button>
              <div className="ml-2 flex items-center gap-0">
                <img
                  src={logo}
                  alt="Bereload Logo"
                  className="transition-all w-10 h-10 ml-1"
                />
                <img
                  src={logoName}
                  alt="Bereload Name"
                  className="w-23 h-8"
                />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto pt-2">
              <SideNavBar isSidebarVisible={isSidebarVisible} />
            </div>
          </div>
        </div>
      )}
      {/* Main content area - this will adjust based on sidebar state */}
  <div className="flex-1 min-w-0 flex flex-col transition-all duration-300 main-container overflow-x-hidden">
        {/* Fixed Header */}
        <header className="h-16 bg-white px-4 sm:px-10 shadow-md flex items-center justify-between sticky top-0 z-10">
          {/* Left section with toggle button and logo for mobile */}
          <div className="flex items-center py-4.5">
            <img
              src={menu}
              alt="Toggle Menu"
              className="w-7 h-7 cursor-pointer"
              onClick={toggleSidebar}
            />
            {/* Show logo and logo name on mobile only */}
            <span className="flex items-center ml-2 md:hidden">
              <img
                src={logo}
                alt="Bereload Logo"
                className="w-8 h-8 ml-1"
              />
              <img
                src={logoName}
                alt="Bereload Name"
                className="h-6 ml-1"
              />
            </span>
          </div>

          {/* Profile Section */}
          <div className="relative px-2">
            <div
              className="flex items-center gap-3 cursor-pointer"
              onClick={toggleDropdown}
              onDoubleClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              {/* Profile Picture/Initials */}
              <div className="w-8 h-8 rounded-full bg-gray-500 text-white flex items-center justify-center text-sm">
                {user.userName[0]?.toUpperCase() || ""}
              </div>
              <div className="hidden sm:block">
                <div className="text-sm text-text1 font-medium">
                  {user.userName}
                </div>
              </div>
            </div>

            {/* Dropdown Menu */}
            {isDropdownVisible && (
              <div className="fixed inset-0 bg-black bg-opacity-25 z-50">
                <div
                  ref={dropdownRef}
                  className="absolute top-16 right-4 sm:right-20 bg-white rounded-md w-40 z-60"
                >
                  <div
                    className="px-4 py-3 text-gray-700 font-normal text-left text-sm cursor-pointer hover:bg-gray-100"
                    onClick={() => {
                      setIsDropdownVisible(false);
                      handleSignOut();
                    }}
                  >
                    Sign out
                  </div>
                </div>
              </div>
            )}
          </div>
        </header>
        {/* Main content - scrollable area below the fixed header */}
        <main className="flex-1 min-h-0 overflow-y-auto bg-white ">
          <Outlet /> {/* Replace children with Outlet */}
        </main>
        <ToastContainer
          position="top-right z-40"
          autoClose={3000}
          hideProgressBar={false}
          closeOnClick
          pauseOnHover
          draggable
          theme="light"
        />
      </div>
    </div>
  );
};

export default AdminLayout;